import { Bounds } from './objectGeometry';

export type GuideOrientation = 'vertical' | 'horizontal';

export interface Guide {
  orientation: GuideOrientation;
  position: number;
}

export interface GuideTargets {
  vertical: number[];
  horizontal: number[];
}

export interface SmartSnap {
  offset: { x: number; y: number };
  guides: Guide[];
}

// Lines that count as aligned once snapped (sub-pixel differences remain
// after zoom and scaling).
const ALIGNED_TOLERANCE = 0.5;

const verticalLines = (bounds: Bounds) => [
  bounds.left,
  bounds.left + bounds.width / 2,
  bounds.left + bounds.width,
];

const horizontalLines = (bounds: Bounds) => [
  bounds.top,
  bounds.top + bounds.height / 2,
  bounds.top + bounds.height,
];

// The slide's edges and center lines, and the edges and centers of the
// other objects.
export const getGuideTargets = (
  slide: Bounds,
  others: Bounds[],
): GuideTargets => {
  const all = [slide, ...others];
  return {
    vertical: all.reduce<number[]>(
      (lines, b) => lines.concat(verticalLines(b)),
      [],
    ),
    horizontal: all.reduce<number[]>(
      (lines, b) => lines.concat(horizontalLines(b)),
      [],
    ),
  };
};

const closestOffset = (
  points: number[],
  targets: number[],
  threshold: number,
) => {
  let best: number | null = null;
  points.forEach((point) =>
    targets.forEach((target) => {
      const distance = target - point;
      if (
        Math.abs(distance) <= threshold &&
        (best === null || Math.abs(distance) < Math.abs(best))
      ) {
        best = distance;
      }
    }),
  );
  return best ?? 0;
};

const alignedTargets = (points: number[], targets: number[], offset: number) =>
  Array.from(
    new Set(
      targets.filter((target) =>
        points.some(
          (point) => Math.abs(target - (point + offset)) < ALIGNED_TOLERANCE,
        ),
      ),
    ),
  );

// How far to move `moving` so its nearest edge or center lands on a target
// line within `threshold`, and which lines it lines up with afterwards.
export const getSmartSnap = (
  moving: Bounds,
  targets: GuideTargets,
  threshold: number,
): SmartSnap => {
  const xs = verticalLines(moving);
  const ys = horizontalLines(moving);
  const x = closestOffset(xs, targets.vertical, threshold);
  const y = closestOffset(ys, targets.horizontal, threshold);
  return {
    offset: { x, y },
    guides: [
      ...alignedTargets(xs, targets.vertical, x).map((position) => ({
        orientation: 'vertical' as const,
        position,
      })),
      ...alignedTargets(ys, targets.horizontal, y).map((position) => ({
        orientation: 'horizontal' as const,
        position,
      })),
    ],
  };
};
